import { useState, useEffect } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { useTranslation } from 'react-i18next'
import {
  Checkbox,
  Input,
  MainContentLayout,
  Confirmation,
  Alert,
  Header,
  Content,
  AddDocumentDialog,
} from '@/components'
import { DocumentTable } from '@/components/DocumentTable'
import { usePageLoader } from '@/components'
import {
  usePreventBackNavigation,
  useNavigationGuard,
} from '@/services/navigation-guard'
import { advanceToStep } from '@/services/navigation'
import { useDeleteDocument } from '@/hooks/useDeleteDocument'
import { useStartSignProcess } from '@/hooks/useStartSignProcess'
import { useGetDocuments } from '@/hooks/useGetDocuments'
import { getConfig } from '../../../config'
import { getProcessId } from '@/services/session'

export function DocumentAddPage() {
  const { t } = useTranslation()
  useLocation()

  // Blokuj cofanie się w przeglądarce podczas tego etapu procesu
  usePreventBackNavigation()

  // Hook do bezpiecznej nawigacji
  const { navigateTo } = useNavigationGuard()

  // Hook do usuwania dokumentów
  const { deleteDocument, isDeleting } = useDeleteDocument()

  // Hook do rozpoczynania procesu podpisu
  const { startSign } = useStartSignProcess()

  // Hook do obsługi page loader
  const { showPageLoader, hidePageLoader } = usePageLoader()

  const { reloadProtectionEnabled } = getConfig()

  // Hook do pobierania dokumentów
  const { documents, isLoading, error, refetch } = useGetDocuments({
    fetchOnMount: !reloadProtectionEnabled && !!getProcessId(),
  })

  // Obsługa błędów przy pobieraniu dokumentów
  const [documentsLoadingError, setDocumentsLoadingError] = useState<
    string | null
  >(null)

  // Aktualizacja stanu błędu, jeśli wystąpił błąd pobierania dokumentów
  useEffect(() => {
    if (error) {
      setDocumentsLoadingError(error)
    }
  }, [error])

  // Removed initial document loading PageLoader effect

  const [noPesel, setNoPesel] = useState(false)
  const [emailEnabled, setEmailEnabled] = useState(false)
  const [email, setEmail] = useState('')
  const [oneTimeConsent, setOneTimeConsent] = useState(false)
  const [commercialConsent, setCommercialConsent] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null)
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(
    null
  )
  const [signErrorMessage, setSignErrorMessage] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [oneTimeConsentError, setOneTimeConsentError] = useState<string | null>(
    null
  )

  const [documentSizes, setDocumentSizes] = useState<Map<string, number>>(
    new Map()
  )

  useEffect(() => {
    if (noPesel && documents.some(d => !d.code)) {
      setNoPesel(false)
    }
  }, [documents, noPesel])

  useEffect(() => {
    setDocumentSizes(prevSizes => {
      const newSizes = new Map(prevSizes)
      const documentIds = new Set(documents.map(doc => doc.id))

      for (const [id] of prevSizes) {
        if (!documentIds.has(id)) {
          newSizes.delete(id)
        }
      }

      return newSizes
    })
  }, [documents])

  const handleContinue = async () => {
    // Sprawdź czy są dokumenty do podpisu
    if (documents.length === 0) {
      return
    }

    // Resetuj błędy walidacji
    setEmailError(null)
    setOneTimeConsentError(null)
    setSignErrorMessage(null)

    if (emailEnabled) {
      let isValid = true
      if (!oneTimeConsent) {
        setOneTimeConsentError(t('documentAdd.checkboxes.consentRequired'))
        isValid = false
      }

      const emailValue = email.trim()
      if (emailValue === '') {
        setEmailError(t('documentAdd.checkboxes.emailRequired'))
        isValid = false
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (emailValue && !emailRegex.test(emailValue)) {
        setEmailError(t('documentAdd.checkboxes.emailInvalid'))
        isValid = false
      }
      if (!isValid) return
    }

    showPageLoader()

    try {
      // Przygotuj zgody marketingowe
      const marketingConsents: string[] = []
      if (oneTimeConsent) marketingConsents.push('oneTimeService')
      if (commercialConsent) marketingConsents.push('commercialInfo')
      if (marketingConsent) marketingConsents.push('marketingPurposes')

      // Rozpocznij proces podpisu
      await startSign({
        email: emailEnabled && email.trim() !== '' ? email : undefined,
        noPesel,
        marketingConsents:
          marketingConsents.length > 0 ? marketingConsents : undefined,
      })

      // Jeśli API call się powiódł, zaawansuj do następnego kroku
      if (advanceToStep('qr-code')) {
        navigateTo('/qr-code')
      }
    } catch (error: any) {
      console.error('Błąd rozpoczynania procesu podpisu:', error)
      setSignErrorMessage(
        error?.response?.data?.detail ??
          t('documentAdd.errors.startSignProcessFailed')
      )
    } finally {
      hidePageLoader()
    }
  }

  const handleAddDocument = () => {
    setShowAddDialog(true)
  }

  const handleAddDocuments = async (documentId: string, fileSize: number) => {
    setDocumentSizes(prevSizes => {
      const newSizes = new Map(prevSizes)
      newSizes.set(documentId, fileSize)
      return newSizes
    })

    await refetch()
  }

  const handleRemoveDocument = (id: string) => {
    setDocumentToDelete(id)
    setShowDeleteConfirmation(true)
  }

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return

    setDeleteErrorMessage(null)

    try {
      // Usuń dokument z API
      await deleteDocument(documentToDelete)

      // Refetch in the background
      refetch()

      // Zamknij dialog tylko po udanym usunięciu dokumentu
      setShowDeleteConfirmation(false)
      setDocumentToDelete(null)
    } catch (error: any) {
      console.error('Błąd usuwania dokumentu:', error)
      setDeleteErrorMessage(
        error?.response?.data?.detail ??
          t('documentAdd.errors.documentDeletionFailed')
      )

      // Pozostaw dialog otwarty, aby wyświetlić komunikat o błędzie
      // Nie resetuj stanu dokumentu do usunięcia
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false)
    setDocumentToDelete(null)
  }

  return (
    <div className='bg-cencert-bg-gray'>
      <Header />

      <Content showSidebar={true}>
        <MainContentLayout
          title={t('documentAdd.title')}
          currentStep={1}
          totalSteps={4}
          {...(documents.length > 0
            ? {
                ctaButtonText: t('documentAdd.button.sign'),
                ctaButtonOnClick: handleContinue,
              }
            : {})}
          showCTA={documents.length > 0}
        >
          {deleteErrorMessage && (
            <Alert
              variant='error'
              message={deleteErrorMessage}
              onDismiss={() => setDeleteErrorMessage(null)}
              className='my-8'
            />
          )}

          {signErrorMessage && (
            <Alert
              variant='error'
              message={signErrorMessage}
              onDismiss={() => setSignErrorMessage(null)}
              className='my-8'
            />
          )}

          {documentsLoadingError && (
            <Alert
              variant='error'
              message={documentsLoadingError}
              onDismiss={() => setDocumentsLoadingError(null)}
              className='my-8'
            />
          )}

          <DocumentTable
            documents={documents}
            onAddDocument={handleAddDocument}
            onRemoveDocument={handleRemoveDocument}
            className='my-8'
            loading={isLoading}
          />

          {documents.length > 0 && (
            <div className='space-y-2 mb-10 flex flex-col gap-2.5'>
              <Checkbox
                checked={noPesel}
                onChange={e =>
                  setNoPesel((e.target as HTMLInputElement).checked)
                }
                label={t('documentAdd.checkboxes.peselLimit')}
                infoText={t('documentAdd.checkboxes.peselLimitInfoTxt')}
                disabled={documents.some(d => !d.code)}
              />
              <div>
                <Checkbox
                  checked={emailEnabled}
                  onChange={e => {
                    const checked = (e.target as HTMLInputElement).checked
                    setEmailEnabled(checked)
                    if (!checked) {
                      setEmail('')
                      setOneTimeConsent(false)
                      setCommercialConsent(false)
                      setMarketingConsent(false)
                      setEmailError(null)
                      setOneTimeConsentError(null)
                    }
                  }}
                  label={t('documentAdd.checkboxes.emailNotification')}
                  infoText={t(
                    'documentAdd.checkboxes.emailNotificationInfoTxt'
                  )}
                />

                {emailEnabled && (
                  <div className='ml-7 mt-2'>
                    <Input
                      type='email'
                      value={email}
                      onChange={e => {
                        setEmail((e.target as HTMLInputElement).value)
                        setEmailError(null)
                      }}
                      placeholder={t('documentAdd.checkboxes.emailPlaceholder')}
                      className='max-w-md'
                      {...(emailError ? { error: emailError } : {})}
                      required
                    />
                  </div>
                )}
              </div>

              {emailEnabled && (
                <div className='flex flex-col gap-5'>
                  <Checkbox
                    checked={oneTimeConsent}
                    onChange={e => {
                      setOneTimeConsent((e.target as HTMLInputElement).checked)
                      setOneTimeConsentError(null)
                    }}
                    label={t('documentAdd.checkboxes.oneTimeService')}
                    required
                    error={!!oneTimeConsentError}
                    {...(oneTimeConsentError && {
                      errorMessage: oneTimeConsentError,
                    })}
                  />
                  <Checkbox
                    checked={commercialConsent}
                    onChange={e => {
                      setCommercialConsent(
                        (e.target as HTMLInputElement).checked
                      )
                    }}
                    label={t('documentAdd.checkboxes.commercialInfo')}
                  />
                  <Checkbox
                    checked={marketingConsent}
                    onChange={e => {
                      setMarketingConsent(
                        (e.target as HTMLInputElement).checked
                      )
                    }}
                    label={t('documentAdd.checkboxes.marketingPurposes')}
                  />
                </div>
              )}
            </div>
          )}
        </MainContentLayout>
      </Content>

      <AddDocumentDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onAddDocuments={handleAddDocuments}
        documents={documents}
        documentSizes={documentSizes}
      />

      <Confirmation
        isOpen={showDeleteConfirmation}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('documentAdd.confirmation.deleteTitle')}
        message={t('documentAdd.confirmation.deleteMessage')}
        confirmText={t('documentAdd.confirmation.deleteConfirm')}
        cancelText={t('documentAdd.confirmation.deleteCancel')}
        variant='danger'
        isLoading={isDeleting}
      />
    </div>
  )
}
